import { useEffect } from 'react';

/**
 * Tracks the virtual keyboard height via `visualViewport` and publishes it as
 * `--keyboard-inset` on `:root`. Fixed-bottom elements can then use
 * `calc(env(safe-area-inset-bottom) + var(--keyboard-inset, 0px))` to stay
 * above the keyboard on iOS Safari / Android Chrome.
 *
 * On iOS Safari, `position:fixed; bottom:0` targets the initial containing
 * block (below the keyboard) rather than the visual viewport. This hook gives
 * other CSS consumers a way to shift those elements up by the keyboard height
 * without requiring JS per-element.
 *
 * The var is 0px (or unset) when the keyboard is closed, so callers can use
 * it as an additive term without special-casing.
 */
export function useViewportKeyboard(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      // `window.innerHeight` is the full viewport height (keyboard excluded
      // on most browsers). `vv.height` shrinks when the keyboard opens.
      const inset = Math.max(0, window.innerHeight - vv.height - (vv.offsetTop ?? 0));
      document.documentElement.style.setProperty('--keyboard-inset', `${inset}px`);
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      document.documentElement.style.removeProperty('--keyboard-inset');
    };
  }, []);
}
